import {Helmet} from '@dr.pogodin/react-helmet';
import {showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteDirectSaleMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  Main,
  catchUnhandledDataGridActions,
  handleApiError,
  queryParams,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function DirectSalesList() {
  const navigate = useNavigate();

  const [directSaleCreate] = usePermissions({
    permissionKeys: ['directSaleCreate'],
  });

  const [deleteDirectSale] = useDeleteDirectSaleMutation();

  const actionCallback: ActionCallback = async ({
    actionKey,
    rowId,
    sourceSearchParams,
    refreshData,
  }) => {
    await match(actionKey)
      .with('redirectDetail', () => {
        navigate(
          composePath(warehouseRoutes.directSalesDetailOverview, {
            queryParams: `${sourceSearchParams}&${queryParams.REDIRECT_SOURCE}=warehouse-direct-sales`,
            params: {
              id: rowId as string,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', async () => {
        const directSaleId = isArray(rowId) ? rowId : [rowId];

        await deleteDirectSale({directSaleId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const handleCreateDirectSaleRedirect = () => {
    navigate(warehouseRoutes.directSalesCreate);
  };

  return (
    <>
      <Helmet title={i18n.t('module.warehouse.title')} />
      <Main
        heading={i18n.t('page.warehouse.labels.directSales')}
        actions={[
          {
            type: 'button',
            title: i18n.t('page.warehouse.labels.newDirectSale'),
            onClick: handleCreateDirectSaleRedirect,
            isDisabled: !directSaleCreate,
            tooltip: !directSaleCreate
              ? i18n.t('module.warehouse.label.missingDirectSaleCreatePermission')
              : undefined,
            'data-testid': testIds.warehouse.directSalesList('newDirectSale'),
          },
        ]}
      >
        <Box height="86vh" width="100%">
          <DmsDataGrid
            gridCode="direct-sale"
            actionCallback={actionCallback}
            data-testid="directSaleDatagrid"
          />
        </Box>
      </Main>
    </>
  );
}
